import { Box, Container, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Button from 'components/Button';
import Icon from 'components/Icon';
import PageFooter from 'components/PageFooter';
import PageFooterMobile from 'components/PageFooterMobile';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useState } from 'react';
import getCleverTap from 'utils/clevertap';

export const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100vh',

    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  main: {
    width: '100%',
  },
  hero: {
    position: 'relative',
    width: '100%',

    '& header': {
      padding: '36px 0 5px 0',

      [theme.breakpoints.down('md')]: {
        padding: '36px 0 24px 0',
      },
    },

    '& h1': {
      color: '#FFF',
      fontSize: '48px',
      fontWeight: 800,
      maxWidth: '494px',
      paddingBottom: '24px',

      [theme.breakpoints.down('md')]: {
        fontSize: '32px',
        paddingBottom: '16px',
      },
    },

    '& p': {
      color: '#FFF',
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '1.47',
      maxWidth: '494px',
      paddingBottom: '40px',

      [theme.breakpoints.down('md')]: {
        fontSize: '18px',
        paddingBottom: '20px',
      },
    },

    '& > span': {
      zIndex: -1,
    },
  },
  heroContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: '12px',

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: '20px',
    },
  },
  callToActionContainer: {
    position: 'relative',
    padding: '40px 0',
    width: '100%',
    marginBottom: '64px',

    '& > span': {
      zIndex: -1,
    },

    '& h2': {
      color: '#D6246E',
      fontSize: '32px',
      fontWeight: 700,
      paddingBottom: '8px',

      [theme.breakpoints.down('md')]: {
        fontSize: '24px',
      },
    },

    '& p': {
      fontSize: '24px',
      color: '#D6246E',

      [theme.breakpoints.down('md')]: {
        fontSize: '18px',
      },
    },
  },
  callToActionBg: {
    zIndex: -1,
  },
  mainContent: {
    display: 'flex',
    gap: '140px',
    paddingBottom: '80px',
    alignItems: 'flex-start',

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: '40px',
    },
  },
  contentHighlights: {
    maxWidth: '414px',
    borderRadius: '14px',
    backgroundColor: '#F3F4F7',
    padding: '34px 20px',

    '& h3': {
      fontSize: '18px',
      fontWeight: 600,
      color: '#000',
    },

    '& ul': {
      listStyle: 'none',
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',

      '& li': {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        color: '#616161',
      },
    },
  },
  checkIcon: {
    color: '#7833FF',
  },
  form: {
    backgroundColor: '#EDE4FE',
    padding: '40px',
    borderRadius: '14px',

    '& h3': {
      fontSize: '20px',
      fontWeight: 600,
      color: '#7833FF',
      marginBottom: '16px',
    },

    '& form': {
      '& MuiFormControl': {
        marginBottom: '8px',
      },

      '& p': {
        fontSize: '14px',
        color: '#616161',
        padding: '24px 0 32px 0',
      },
    },
  },
  desktopFooter: {
    display: 'block',
    width: '100%',

    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  mobileFooter: {
    display: 'block',
    width: '100%',

    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}));

const Props = {
  ebook: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    bannerTitle: PropTypes.string.isRequired,
    ctaVariant: PropTypes.string.isRequired,
    bgImage: PropTypes.shape({
      src: PropTypes.string.isRequired,
    }).isRequired,
    img: PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }).isRequired,
    downloadUrl: PropTypes.string.isRequired,
    contentHighlights: PropTypes.shape({
      title: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(PropTypes.string).isRequired,
      subtitle: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default function EbookLandingPageLayout({ ebook }) {
  const clevertap = getCleverTap();

  const router = useRouter();
  const classes = useStyles();

  const [errorFields, setErrorFields] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: { value: '', required: true },
    email: { value: '', required: true },
    role: { value: '', required: true },
    institution: { value: '', required: false },
  });

  const handleScrollToForm = () => {
    window.scrollTo({
      top: document.querySelector('#cta-banner').offsetTop,
      behavior: 'smooth',
    });
  };

  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: {
        value: event.target.value,
        required: event.target.required,
      },
    });
  };

  const downloadEbook = () => {
    const link = document.createElement('a');
    link.href = `/files/ebooks/${ebook.downloadUrl}`;
    link.download = ebook.downloadUrl.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = Object.keys(formData).filter((key) => {
      if (formData[key].required && !formData[key].value) {
        return key;
      }
    });

    setErrorFields(errors);

    if (errors.length > 0) {
      return;
    }

    clevertap.onUserLogin.push({
      Site: {
        Name: formData.name.value,
        Email: formData.email.value,
        'MSG-email': true,
        'MSG-push': true,
      },
    });

    clevertap.addMultiValueForKey('Ebook Type MTG', ebook.title);

    clevertap.profile.push({
      Site: {
        'Ebook MTG': 'Yes',
        Role: formData.role.value,
        Organization: formData.institution.value || undefined,
      },
    });

    clevertap.event.push('Download Done', {
      Product: 'MTG',
      Email: formData.email.value,
      Type: ebook.title,
      URL: router.asPath,
      Locale: 'Ebook Page',
      Description: 'Ebook Downloaded',
    });

    downloadEbook();
    setSubmitted(true);
  };

  return (
    <Box className={classes.root}>
      <Head>
        <title>{ebook.title} - Mind The Graph</title>
        <meta name="description" content={ebook.description} />
      </Head>

      <Box className={classes.hero}>
        <Container>
          <header>
            <Link href="/" passHref>
              <a>
                <Image
                  src="/images/mtg-logo-editage.png"
                  alt="Mind the Graph"
                  width="135"
                  height="45"
                />
              </a>
            </Link>
          </header>

          <Box className={classes.heroContent}>
            <Box>
              <Typography component="h1" variant="h1">
                {ebook.title}
              </Typography>

              <Typography component="p" variant="body1">
                {ebook.description}
              </Typography>

              <Box maxWidth="474px">
                <Button onClick={handleScrollToForm} variant={ebook.ctaVariant}>
                  Download It Now
                </Button>
              </Box>
            </Box>

            <Image
              src={`/images/ebooks/${ebook.img.src}`}
              alt={ebook.img.alt}
              width={ebook.img.width}
              height={ebook.img.height}
            />
          </Box>
        </Container>

        <Image
          src={`/images/ebooks/${ebook.bgImage.src}`}
          alt=""
          layout="fill"
          objectFit="cover"
          quality={100}
        />
      </Box>

      <mainWrapper className={classes.main}>
        <Box id="cta-banner" className={classes.callToActionContainer}>
          <Image
            src="/images/ebooks/bannerBg.png"
            alt=""
            layout="fill"
            objectFit="cover"
            quality={100}
            className={classes.callToActionBg}
          />

          <Container>
            <Typography component="h2" variant="h2" textAlign="center">
              {ebook.bannerTitle}
            </Typography>

            <Typography component="p" variant="body1" textAlign="center">
              Download the ebook now.
            </Typography>
          </Container>
        </Box>

        <Container className={classes.mainContent}>
          <Box className={classes.contentHighlights}>
            <Typography component="h3" variant="h3">
              {ebook.contentHighlights.title}
            </Typography>

            <ul>
              {ebook.contentHighlights.items.map((item) => (
                <li key={item}>
                  <Box className={classes.checkIcon}>
                    <Icon name="check" className={classes.checkIcon} />
                  </Box>
                  {item}
                </li>
              ))}
            </ul>

            <Typography component="p" variant="body1">
              {ebook.contentHighlights.subtitle}
            </Typography>
          </Box>

          <Box className={classes.form}>
            <Typography component="h3" variant="h3">
              Fill the form and download your Ebook!
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                label="Name*"
                name="name"
                variant="outlined"
                margin="normal"
                style={{ backgroundColor: '#FFF' }}
                value={formData.name.value}
                onChange={handleChange}
                error={errorFields.includes('name')}
                fullWidth
              />

              <TextField
                label="E-mail*"
                name="email"
                variant="outlined"
                margin="normal"
                style={{ backgroundColor: '#FFF' }}
                value={formData.email.value}
                onChange={handleChange}
                type="email"
                error={errorFields.includes('email')}
                fullWidth
              />

              <TextField
                label="Role*"
                name="role"
                variant="outlined"
                margin="normal"
                style={{ backgroundColor: '#FFF' }}
                value={formData.role.value}
                onChange={handleChange}
                error={errorFields.includes('role')}
                fullWidth
              />

              <TextField
                label="Institution"
                name="institution"
                variant="outlined"
                margin="normal"
                style={{ backgroundColor: '#FFF' }}
                value={formData.institution.value}
                onChange={handleChange}
                fullWidth
              />

              <Typography component="p" variant="body1">
                Fields marked with an asterisk * are required.
              </Typography>

              <Button type="submit" variant="primary" disabled={submitted}>
                Download It Now
              </Button>
            </form>
          </Box>
        </Container>
      </mainWrapper>

      <Box className={classes.desktopFooter}>
        <PageFooter />
      </Box>

      <Box className={classes.mobileFooter}>
        <PageFooterMobile />
      </Box>
    </Box>
  );
}

EbookLandingPageLayout.propTypes = Props;
